// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { Button, Divider, Modal } from "antd";
import { BarChartOutlined, StarFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { SurveyResultsResponseDTO } from "../../../../../api/backend-openapi";
import { requestGetSurveyResults } from "../../../../../state/survey/SurveysEffects";
import { SurveyInfoResponseDTO } from "../../../../../api/backend-openapi";

export interface SurveyResultsButtonProps {
  surveyDetails: SurveyInfoResponseDTO;
}

const SurveyResultsButton: React.FunctionComponent<SurveyResultsButtonProps> = (
  props: SurveyResultsButtonProps
) => {
  const { surveyDetails } = props;

  const [results, setResults] = useState<SurveyResultsResponseDTO | null>(null);

  useEffect(() => {
    if (surveyDetails) {
      requestGetSurveyResults(surveyDetails.id);
    }
  }, [surveyDetails]);

  useEffect(() => {
    return requestGetSurveyResults.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setResults(result.data);
      }
    });
  });

  const { t } = useTranslation();

  const [visible, setVisible] = useState<boolean>(false);

  const innerButton = (
    <Button
      type="default"
      className="btn-secondary m-2"
      onClick={() => setVisible(true)}
    >
      <BarChartOutlined />
      {t("detail_survey.results")}
    </Button>
  );

  return (
    <>
      {innerButton}
      <Modal
        className="results_survey"
        title={t("detail_survey.results_resume")}
        visible={visible}
        centered
        footer={
          <Button key="closeResultsModal" onClick={() => setVisible(false)}>
            {t("global.close")}
          </Button>
        }
        onCancel={() => setVisible(false)}
        bodyStyle={{ padding: 0 }}
      >
        <div className="preview-theme ml-1">
          <div className="ml-1">
            <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
              <h4 className="text-primary mb-0">
                {t("detail_survey.number_votes", {
                  count: results?.nbResponses || 0
                })}
              </h4>
            </Divider>
            {surveyDetails.questions.map(question => {
              const questionId = question?.id;
              return questionId && results && results.responses ? (
                <div key={`result_question_${questionId}`}>
                  <Divider orientation="left" className="mb-0 mb-sm-3 mt-3" />
                  <h4 className="text-primary mb-0 mb-sm-3 mt-3 ml-3 mr-3">
                    {question.label}
                  </h4>
                  {question.type === "freetext" && (
                    <h5 className="text-primary mb-0 mb-sm-3 mt-3 ml-3 mr-3">
                      {t("detail_survey.free_text_question_no_result_stat")}
                    </h5>
                  )}
                  {question.type !== "freetext" &&
                    question.answerChoice.map(answerChoice => {
                      const answerId = answerChoice?.id;
                      if (answerId === undefined) {
                        return "";
                      }

                      const answersFromUsers = results.responses[questionId];
                      if (answersFromUsers === undefined) {
                        return "";
                      }

                      const nbResponseForAnswer =
                        results.responses[questionId][answerId];

                      const barPadding = 8;
                      const barHeight = 25;
                      const totalWidth = 300;
                      const percentage =
                        (nbResponseForAnswer / results.nbResponses) * 100;

                      return (
                        <div
                          className="text-primary ml-5"
                          key={`result_answer_${questionId}_${answerId}`}
                        >
                          <div>
                            {question.type === "mark"
                              ? Array.apply(
                                  0,
                                  Array(Number.parseInt(answerChoice.label, 10))
                                ).map(() => (
                                  <StarFilled
                                    key={`star_${questionId}_${answerChoice.label}`}
                                  />
                                ))
                              : answerChoice.label}
                          </div>
                          <svg width={totalWidth} height={barHeight}>
                            <g className="bar-group">
                              <rect
                                x={0}
                                y={barPadding}
                                width={totalWidth}
                                height={barHeight - barPadding}
                                fill="#DAEDF4"
                              />
                              <rect
                                x={0}
                                y={barPadding}
                                width={(percentage * totalWidth) / 100}
                                height={barHeight - barPadding}
                                fill="#348AA7"
                              />
                            </g>
                          </svg>
                          <span className="ml-3">
                            {percentage.toFixed(0)} % ({nbResponseForAnswer})
                          </span>
                        </div>
                      );
                    })}
                </div>
              ) : (
                ""
              );
            })}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SurveyResultsButton;
